import { ImageUploadBox } from '@shared/ui/ImageUploadBox/ImageUploadBox';
import { CheckCircle } from '@phosphor-icons/react';
import { useState } from 'react';

import { useRequestDepositMutation, type PaymentMethodDto } from '@entities/deposit';
import { apiErrorMessage } from '@shared/api/baseApi';
import { compressImage, formatBytes } from '@shared/lib/compressImage';
import { haptics } from '@shared/lib/telegram';
import { Button } from '@shared/ui/Button';
import { PaymentMethods } from '@widgets/PaymentMethods';
import styles from './DepositForm.module.css';

const MAX_RECEIPT_BYTES = 20 * 1024 * 1024;

export function DepositForm({
  methods,
}: {
  methods: PaymentMethodDto[];
}) {
  const [receipt, setReceipt] = useState<{
    name: string;
    dataUrl: string;
    sizeLabel: string;
  } | null>(null);
  const [receiptText, setReceiptText] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{
    amount?: string;
    mode: 'automatic' | 'manual';
    balance?: string;
    duplicate: boolean;
  } | null>(null);
  const [requestDeposit, { isLoading }] = useRequestDepositMutation();

  const receiptReady = Boolean(receipt) && !compressing;
  const textReady = receiptText.trim().length > 0;
  const evidenceReady = receiptReady || textReady;

  const pickFile = async (file: File) => {
    if (file.size > MAX_RECEIPT_BYTES) {
      setError('Choose an image under 20 MB.');
      return;
    }

    setCompressing(true);
    setError(null);

    try {
      const result = await compressImage(file);
      const shrunk = result.compressedBytes < result.originalBytes;
      setReceipt({
        name: file.name,
        dataUrl: result.dataUrl,
        sizeLabel: shrunk
          ? `${formatBytes(result.originalBytes)} → ${formatBytes(result.compressedBytes)}`
          : formatBytes(result.compressedBytes),
      });
      haptics.select();
    } catch {
      setError('Could not read that image. Choose a JPEG, PNG, WebP or GIF receipt.');
    } finally {
      setCompressing(false);
    }
  };

  const submit = async () => {
    if (!evidenceReady) {
      setError('Paste the payment message or upload your receipt to continue.');
      return;
    }
    if (compressing) return;

    setError(null);
    try {
      const response = await requestDeposit({
        ...(receipt ? { receiptBase64: receipt.dataUrl } : {}),
        ...(receiptText.trim() ? { receiptText: receiptText.trim() } : {}),
      }).unwrap();
      haptics.notify('success');
      setSubmitted({
        mode: response.mode,
        duplicate: response.duplicate === true,
        ...(response.mode === 'automatic' ? { amount: response.deposit.amount.label } : {}),
        ...(response.balance ? { balance: response.balance.label } : {}),
      });
      setReceipt(null);
      setReceiptText('');
    } catch (cause) {
      haptics.notify('error');
      setError(apiErrorMessage(cause, 'Could not submit your deposit. Please try again.'));
    }
  };

  if (submitted) {
    return (
      <div className={styles.success} role="status">
        <CheckCircle size={42} weight="fill" />
        <h3>
          {submitted.duplicate
            ? 'Payment already verified'
            : submitted.mode === 'automatic'
              ? 'Payment verified'
              : 'Deposit submitted'}
        </h3>
        <p>
          {submitted.duplicate
            ? `This payment was previously added to your wallet${submitted.balance ? `. Current balance: ${submitted.balance}.` : '.'}`
            : submitted.mode === 'automatic'
            ? `${submitted.amount ?? 'Your payment'} was added to your wallet${submitted.balance ? `. New balance: ${submitted.balance}.` : '.'}`
            : 'Your receipt is under review. An admin will confirm the detected amount or correct it before approval.'}
        </p>
        <Button variant="secondary" onClick={() => setSubmitted(null)}>
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <section className={styles.section} aria-labelledby="payment-account-title">
        <div className={styles.sectionHeading}>
          <h3 id="payment-account-title">Payment account</h3>
          <span>Tap to view details</span>
        </div>
        <PaymentMethods methods={methods} />
      </section>

      <section className={styles.section} aria-labelledby="receipt-title">
        <div className={styles.sectionHeading}>
          <h3 id="receipt-title">Payment confirmation</h3>
          <span>Paste message or upload</span>
        </div>

        <label className={styles.messageLabel} htmlFor="payment-message">
          Telebirr or CBE message/link
        </label>
        <textarea
          id="payment-message"
          className={styles.messageInput}
          placeholder="Paste the payment SMS, including its receipt link"
          value={receiptText}
          rows={4}
          onChange={(event) => {
            setReceiptText(event.target.value);
            setError(null);
          }}
        />

        <div className={styles.orDivider}><span>or upload a screenshot</span></div>

        <ImageUploadBox label="Payment receipt" hint={receipt ? `${receipt.name} · ${receipt.sizeLabel}` : 'Upload a clear screenshot of your payment.'} accept="image/jpeg,image/png,image/webp,image/gif" maxSizeMB={20} previewUrl={receipt?.dataUrl} busy={compressing} disabled={isLoading} removable onChange={file => { if (file) void pickFile(file); else { setReceipt(null); setError(null); } }} />
      </section>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        fullWidth
        loading={isLoading}
        // Keep validation in submit(). Disabling this control for an invalid
        // field leaves Telegram WebView users with no feedback and, after an
        // image pick, can make the form appear permanently stuck.
        disabled={compressing}
      >
        {evidenceReady ? 'Submit payment' : 'Submit deposit'}
      </Button>
      <p className={styles.reviewNote}>
        Verified payments update instantly. Other screenshots go to manual review.
      </p>
    </form>
  );
}
